import type { SegmentConditionGroups } from '@reach/shared-types/conditions';
import { generateId } from '@/lib/generate-id';
import React, { useEffect, useRef, useState } from 'react';
import { ConditionGroupList } from './ConditionGroupList';
import { useSegmentSchemaCatalog } from './useSegmentSchemaCatalog';
import { builderStateToConditionGroups, conditionGroupsToBuilderState } from './segment-builder.utils';
import type { BuilderState } from './segment-builder.types';
import styles from './SegmentVisualBuilder.module.css';

interface SegmentVisualBuilderProps {
  conditionGroups: SegmentConditionGroups | null;
  onChange: (groups: SegmentConditionGroups) => void;
  onUnsupported: (reason: string) => void;
}

/**
 * Visual segment builder. Converts condition groups JSON to/from internal builder state.
 * State is initialized from conditionGroups when available fields load; subsequent
 * prop changes do not overwrite local state (parent should key this component when
 * switching modes so it remounts with fresh initial state).
 */
export const SegmentVisualBuilder: React.FC<SegmentVisualBuilderProps> = ({
  conditionGroups,
  onChange,
  onUnsupported,
}) => {
  const { schemaCatalog, isLoading, isRetrying, error, retry } = useSegmentSchemaCatalog();
  const [builderState, setBuilderState] = useState<BuilderState>(() => ({
    groups: [],
  }));
  const lastEmittedGroupsRef = useRef<string | null>(null);

  useEffect(() => {
    // A refreshed catalog may change whether stored relationships are available.
    lastEmittedGroupsRef.current = null;
  }, [schemaCatalog]);

  useEffect(() => {
    if (isLoading || !schemaCatalog) return;
    const groups = conditionGroups ?? [];
    const groupsKey = JSON.stringify(groups);
    if (groupsKey === lastEmittedGroupsRef.current) return;
    lastEmittedGroupsRef.current = groupsKey;
    const conversionResult = conditionGroupsToBuilderState(groups, schemaCatalog);
    if (conversionResult.kind === 'unsupported') {
      onUnsupported(conversionResult.reason);
      return;
    }
    const state = conversionResult.state;
    if (state.groups.length === 0) {
      state.groups = [
        {
          id: generateId(),
          logic: 'and',
          conditions: [],
        },
      ];
    }
    setBuilderState(state);
  }, [schemaCatalog, conditionGroups, isLoading, onUnsupported]);

  const handleUpdate = (nextState: BuilderState) => {
    setBuilderState(nextState);
    const groups = builderStateToConditionGroups(nextState);
    lastEmittedGroupsRef.current = JSON.stringify(groups);
    onChange(groups);
  };

  if (error) {
    return (
      <div className={styles.emptyBuilder}>
        <p>Couldn’t load fields. Please try again.</p>
        <button type="button" className={styles.addConditionBtn} onClick={retry} disabled={isRetrying}>
          Retry
        </button>
      </div>
    );
  }

  if (isLoading || !schemaCatalog) {
    return (
      <div className={styles.emptyBuilder}>
        <p>Loading fields...</p>
      </div>
    );
  }

  if (builderState.groups.length === 0) {
    return (
      <div className={styles.emptyBuilder}>
        <p>No condition groups. Add a group to get started.</p>
      </div>
    );
  }

  return <ConditionGroupList state={builderState} onUpdate={handleUpdate} disabled={isLoading} />;
};
